import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import { fetchCurrentAuthUser } from '@/shell/services/auth'
import { getHomePath } from '@/shell/module-system'
import type {
  AdminRolesResponse,
  AdminUser,
  AdminUsersResponse,
  ExportLaneCreateResponse,
  ExportLaneInput,
  ExportLaneListResponse,
  FreightPipelineEvent,
  FreightPipelineNotificationsResponse,
  NotificationEvent,
  NotificationsResponse,
  OperationalAlertEvent,
  OperationalAlertsResponse,
  QuoteNotificationEvent,
  QuoteNotificationsResponse,
  SalesPipelineEvent,
  SalesPipelineNotificationsResponse,
  UserRole,
  VendorPriceChangeEvent,
  VendorPriceChangeNotificationsResponse,
  WarehouseIncidentEvent,
  WarehouseIncidentNotificationsResponse,
  WorkersHealth,
} from './types'

export function useAdminUsers() {
  return useQuery({
    queryKey: ['settings', 'users'],
    queryFn: () => jsonFetch<AdminUsersResponse>('/api/admin/users'),
    staleTime: 30_000,
  })
}

export function useAdminRoles() {
  return useQuery({
    queryKey: ['settings', 'roles'],
    queryFn: () => jsonFetch<AdminRolesResponse>('/api/admin/roles'),
    staleTime: 5 * 60_000,
  })
}

export interface UpsertUserVars {
  email: string
  role: UserRole
  name?: string | null
  /** Sprint 16 follow-up — optional Odoo login override. */
  odoo_login?: string | null
}

export function useUpsertUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: UpsertUserVars) =>
      jsonFetch<unknown>('/api/admin/users', {
        method: 'POST',
        body: vars,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'users'] })
    },
  })
}

export function useDeleteUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (email: string) =>
      jsonFetch<unknown>(`/api/admin/users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'users'] })
    },
  })
}

export interface UpdateUserVars {
  /** Current email — used as the path key, unchanged if not renaming. */
  email: string
  name?: string | null
  new_email?: string | null
  odoo_login?: string | null
}

export interface UpdateUserResponse {
  success: true
  self_rename: boolean
  user: AdminUser
}

export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ email, ...body }: UpdateUserVars) =>
      jsonFetch<UpdateUserResponse>(`/api/admin/users/${encodeURIComponent(email)}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'users'] })
    },
  })
}

// ─── Vendor password (admin set / reset) ────────────────────────────

export interface SetPasswordVars {
  email: string
  new_password: string
}

export function useSetUserPassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ email, new_password }: SetPasswordVars) =>
      jsonFetch<{ ok: true; email: string }>(
        `/api/admin/users/${encodeURIComponent(email)}/set-password`,
        { method: 'POST', body: { new_password } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'users'] })
    },
  })
}

// ─── Connect-As (admin "view-as-user") ──────────────────────────────

export interface ConnectAsResponse {
  ok: true
  target_email: string
  target_name: string
  started_at: number
}

export function useConnectAs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (email: string) =>
      jsonFetch<ConnectAsResponse>(`/api/admin/impersonate/${encodeURIComponent(email)}`, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] })
      // Hard navigate so any module-local cached state for the admin's
      // identity gets re-fetched as the impersonated user. The target
      // isn't always '/' — a role like outside_sales_rep has no
      // Dashboard access, so `getHomePath` resolves against the fresh
      // (impersonated) identity rather than assuming Dashboard's route.
      const impersonatedUser = await fetchCurrentAuthUser()
      window.location.assign(getHomePath(impersonatedUser))
    },
  })
}

export function useNotificationSubscribers() {
  return useQuery({
    queryKey: ['settings', 'notifications'],
    queryFn: () => jsonFetch<NotificationsResponse>('/api/cofa/notifications/subscribers'),
    staleTime: 30_000,
  })
}

export interface AddSubscriberVars {
  email: string
  event: NotificationEvent
  active?: boolean
}

export function useAddSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: AddSubscriberVars) =>
      jsonFetch<unknown>('/api/cofa/notifications/subscribers', {
        method: 'POST',
        body: { active: true, ...vars },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'notifications'] })
    },
  })
}

export function useDeleteSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`/api/cofa/notifications/subscribers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'notifications'] })
    },
  })
}

// ─── Quote notification subscribers (Sprint 12 Chunk 11) ────────────

export function useQuoteNotificationSubscribers() {
  return useQuery({
    queryKey: ['settings', 'quote-notifications'],
    queryFn: () => jsonFetch<QuoteNotificationsResponse>('/api/quotes/notifications/subscribers'),
    staleTime: 30_000,
  })
}

export interface AddQuoteSubscriberVars {
  email: string
  event: QuoteNotificationEvent
  active?: boolean
}

export function useAddQuoteSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: AddQuoteSubscriberVars) =>
      jsonFetch<unknown>('/api/quotes/notifications/subscribers', {
        method: 'POST',
        body: { active: true, ...vars },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'quote-notifications'],
      })
    },
  })
}

export function useDeleteQuoteSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`/api/quotes/notifications/subscribers/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'quote-notifications'],
      })
    },
  })
}

// ─── Sales Pipeline notification subscribers (Sprint 22 Chunk 6) ────

export function useSalesPipelineSubscribers() {
  return useQuery({
    queryKey: ['settings', 'sales-pipeline-notifications'],
    queryFn: () =>
      jsonFetch<SalesPipelineNotificationsResponse>(
        '/api/sales-pipeline/notifications/subscribers',
      ),
    staleTime: 30_000,
  })
}

export interface AddSalesPipelineSubscriberVars {
  email: string
  event: SalesPipelineEvent
  active?: boolean
}

export function useAddSalesPipelineSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: AddSalesPipelineSubscriberVars) =>
      jsonFetch<unknown>('/api/sales-pipeline/notifications/subscribers', {
        method: 'POST',
        body: { active: true, ...vars },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'sales-pipeline-notifications'],
      })
    },
  })
}

export function useDeleteSalesPipelineSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`/api/sales-pipeline/notifications/subscribers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'sales-pipeline-notifications'],
      })
    },
  })
}

// ─── Freight Pipeline notification subscribers (Logistics overhaul) ─

export function useFreightPipelineSubscribers() {
  return useQuery({
    queryKey: ['settings', 'freight-pipeline-notifications'],
    queryFn: () =>
      jsonFetch<FreightPipelineNotificationsResponse>(
        '/api/freight-pipeline/notifications/subscribers',
      ),
    staleTime: 30_000,
  })
}

export interface AddFreightPipelineSubscriberVars {
  email: string
  event: FreightPipelineEvent
  active?: boolean
}

export function useAddFreightPipelineSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: AddFreightPipelineSubscriberVars) =>
      jsonFetch<unknown>('/api/freight-pipeline/notifications/subscribers', {
        method: 'POST',
        body: { active: true, ...vars },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'freight-pipeline-notifications'],
      })
    },
  })
}

export function useDeleteFreightPipelineSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`/api/freight-pipeline/notifications/subscribers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'freight-pipeline-notifications'],
      })
    },
  })
}

// ─── Warehouse Incident notification subscribers ─────────────────────

export function useWarehouseIncidentSubscribers() {
  return useQuery({
    queryKey: ['settings', 'warehouse-incident-notifications'],
    queryFn: () =>
      jsonFetch<WarehouseIncidentNotificationsResponse>(
        '/api/warehouse-incidents/notifications/subscribers',
      ),
    staleTime: 30_000,
  })
}

export interface AddWarehouseIncidentSubscriberVars {
  email: string
  event: WarehouseIncidentEvent
  active?: boolean
}

export function useAddWarehouseIncidentSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: AddWarehouseIncidentSubscriberVars) =>
      jsonFetch<unknown>('/api/warehouse-incidents/notifications/subscribers', {
        method: 'POST',
        body: { active: true, ...vars },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'warehouse-incident-notifications'],
      })
    },
  })
}

export function useDeleteWarehouseIncidentSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`/api/warehouse-incidents/notifications/subscribers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'warehouse-incident-notifications'],
      })
    },
  })
}

// ─── Inventory discrepancy report notifications ───────────────────

export function useInventoryDiscrepancySubscribers() {
  return useQuery({
    queryKey: ['settings', 'inventory-discrepancy-notifications'],
    queryFn: () =>
      jsonFetch<import('./types').InventoryDiscrepancyNotificationsResponse>(
        '/api/inventory-discrepancy-reports/notifications/subscribers',
      ),
    staleTime: 30_000,
  })
}

export interface AddInventoryDiscrepancySubscriberVars {
  email: string
  event: import('./types').InventoryDiscrepancyEvent
}

export function useAddInventoryDiscrepancySubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: AddInventoryDiscrepancySubscriberVars) =>
      jsonFetch<import('./types').InventoryDiscrepancySubscriber>(
        '/api/inventory-discrepancy-reports/notifications/subscribers',
        { method: 'POST', body: vars },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'inventory-discrepancy-notifications'],
      })
    },
  })
}

export function useDeleteInventoryDiscrepancySubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`/api/inventory-discrepancy-reports/notifications/subscribers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'inventory-discrepancy-notifications'],
      })
    },
  })
}

// ─── Returns / RMA notifications ───────────────────────────────────

export function useRmaSubscribers() {
  return useQuery({
    queryKey: ['settings', 'rma-notifications'],
    queryFn: () =>
      jsonFetch<import('./types').RmaNotificationsResponse>('/api/rma/notifications/subscribers'),
    staleTime: 30_000,
  })
}

export interface AddRmaSubscriberVars {
  email: string
  event: import('./types').RmaEvent
}

export function useAddRmaSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: AddRmaSubscriberVars) =>
      jsonFetch<import('./types').RmaSubscriber>('/api/rma/notifications/subscribers', {
        method: 'POST',
        body: vars,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'rma-notifications'] })
    },
  })
}

export function useDeleteRmaSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`/api/rma/notifications/subscribers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'rma-notifications'] })
    },
  })
}

// ─── Disciplinary report notifications ────────────────────────────

export function useDisciplinarySubscribers() {
  return useQuery({
    queryKey: ['settings', 'disciplinary-notifications'],
    queryFn: () =>
      jsonFetch<import('./types').DisciplinaryNotificationsResponse>(
        '/api/disciplinary-reports/notifications/subscribers',
      ),
    staleTime: 30_000,
  })
}

export interface AddDisciplinarySubscriberVars {
  email: string
  event: import('./types').DisciplinaryEvent
}

export function useAddDisciplinarySubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: AddDisciplinarySubscriberVars) =>
      jsonFetch<import('./types').DisciplinarySubscriber>(
        '/api/disciplinary-reports/notifications/subscribers',
        { method: 'POST', body: vars },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'disciplinary-notifications'],
      })
    },
  })
}

export function useDeleteDisciplinarySubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`/api/disciplinary-reports/notifications/subscribers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'disciplinary-notifications'],
      })
    },
  })
}

// ─── Sample Request assignable reps (admin-managed) ───────────────

export function useSampleRequestReps() {
  return useQuery({
    queryKey: ['settings', 'sample-request-reps'],
    queryFn: () =>
      jsonFetch<import('./types').SampleRequestRepsResponse>(
        '/api/sample-requests/reps?include_inactive=true',
      ),
    staleTime: 30_000,
  })
}

export interface AddSampleRequestRepVars {
  name: string
  email: string
}

export function useAddSampleRequestRep() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: AddSampleRequestRepVars) =>
      jsonFetch<import('./types').SampleRequestRep>('/api/sample-requests/reps', {
        method: 'POST',
        body: vars,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'sample-request-reps'] })
    },
  })
}

export function useDeleteSampleRequestRep() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`/api/sample-requests/reps/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'sample-request-reps'] })
    },
  })
}

// ─── SPI Sample Order notifications ───────────────────────────────

export function useSampleOrderSubscribers() {
  return useQuery({
    queryKey: ['settings', 'sample-order-notifications'],
    queryFn: () =>
      jsonFetch<import('./types').SampleOrderNotificationsResponse>(
        '/api/sample-orders/notifications/subscribers',
      ),
    staleTime: 30_000,
  })
}

export function useAddSampleOrderSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { email: string; event: import('./types').SampleOrderEvent }) =>
      jsonFetch<import('./types').SampleOrderSubscriber>(
        '/api/sample-orders/notifications/subscribers',
        { method: 'POST', body: vars },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'sample-order-notifications'] })
    },
  })
}

export function useDeleteSampleOrderSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`/api/sample-orders/notifications/subscribers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'sample-order-notifications'] })
    },
  })
}

// ─── Sample Request notifications ─────────────────────────────────

export function useSampleRequestSubscribers() {
  return useQuery({
    queryKey: ['settings', 'sample-request-notifications'],
    queryFn: () =>
      jsonFetch<import('./types').SampleRequestNotificationsResponse>(
        '/api/sample-requests/notifications/subscribers',
      ),
    staleTime: 30_000,
  })
}

export interface AddSampleRequestSubscriberVars {
  email: string
  event: import('./types').SampleRequestEvent
}

export function useAddSampleRequestSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: AddSampleRequestSubscriberVars) =>
      jsonFetch<import('./types').SampleRequestSubscriber>(
        '/api/sample-requests/notifications/subscribers',
        { method: 'POST', body: vars },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'sample-request-notifications'],
      })
    },
  })
}

export function useDeleteSampleRequestSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`/api/sample-requests/notifications/subscribers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'sample-request-notifications'],
      })
    },
  })
}

// ─── Uviva submission notifications ────────────────────────────────

export function useUvivaSubmissionSubscribers() {
  return useQuery({
    queryKey: ['settings', 'uviva-submission-notifications'],
    queryFn: () =>
      jsonFetch<import('./types').UvivaSubmissionNotificationsResponse>(
        '/api/v1/uviva/notifications/subscribers',
      ),
    staleTime: 30_000,
  })
}

export interface AddUvivaSubmissionSubscriberVars {
  email: string
  event: import('./types').UvivaSubmissionEvent
}

export function useAddUvivaSubmissionSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: AddUvivaSubmissionSubscriberVars) =>
      jsonFetch<import('./types').UvivaSubmissionSubscriber>(
        '/api/v1/uviva/notifications/subscribers',
        { method: 'POST', body: vars },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'uviva-submission-notifications'],
      })
    },
  })
}

export function useDeleteUvivaSubmissionSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`/api/v1/uviva/notifications/subscribers/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'uviva-submission-notifications'],
      })
    },
  })
}

// ─── Vendor Price Change notification subscribers (Sprint 37) ──────

export function useVendorPriceChangeSubscribers() {
  return useQuery({
    queryKey: ['settings', 'vendor-price-change-notifications'],
    queryFn: () =>
      jsonFetch<VendorPriceChangeNotificationsResponse>(
        '/api/vendor-price-changes/notifications/subscribers',
      ),
    staleTime: 30_000,
  })
}

export interface AddVendorPriceChangeSubscriberVars {
  email: string
  event: VendorPriceChangeEvent
  active?: boolean
}

export function useAddVendorPriceChangeSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: AddVendorPriceChangeSubscriberVars) =>
      jsonFetch<unknown>('/api/vendor-price-changes/notifications/subscribers', {
        method: 'POST',
        body: { active: true, ...vars },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'vendor-price-change-notifications'],
      })
    },
  })
}

export function useDeleteVendorPriceChangeSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`/api/vendor-price-changes/notifications/subscribers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'vendor-price-change-notifications'],
      })
    },
  })
}

export function useWorkersHealth() {
  return useQuery({
    queryKey: ['settings', 'health', 'workers'],
    // Uses the /api/-prefixed mirror so the host-nginx /api/ proxy
    // reaches the middleware. Previously hit /health/workers
    // unprefixed, which nginx routed to the ops SPA → SPA fallback
    // returned index.html → JSON.parse yielded {} → the System
    // Health panel permanently showed UNHEALTHY (fixed 2026-04-24).
    queryFn: () => jsonFetch<WorkersHealth>('/api/health/workers'),
    refetchInterval: 30_000,
    staleTime: 15_000,
  })
}

// ─── Operational Alert notification subscribers (2026-07-23) ────────

export function useOperationalAlertSubscribers() {
  return useQuery({
    queryKey: ['settings', 'operational-alerts-notifications'],
    queryFn: () =>
      jsonFetch<OperationalAlertsResponse>('/api/operational-alerts/notifications/subscribers'),
    staleTime: 30_000,
  })
}

export interface AddOperationalAlertSubscriberVars {
  email: string
  event: OperationalAlertEvent
  active?: boolean
}

export function useAddOperationalAlertSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: AddOperationalAlertSubscriberVars) =>
      jsonFetch<unknown>('/api/operational-alerts/notifications/subscribers', {
        method: 'POST',
        body: { active: true, ...vars },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'operational-alerts-notifications'],
      })
    },
  })
}

export function useDeleteOperationalAlertSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`/api/operational-alerts/notifications/subscribers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'operational-alerts-notifications'],
      })
    },
  })
}

// ─── Export Lane Config (Export Documentation Packet, 2026-09) ─────

const EXPORT_LANES_KEY = ['settings', 'export-lanes'] as const

export function useExportLanes() {
  return useQuery({
    queryKey: EXPORT_LANES_KEY,
    queryFn: () => jsonFetch<ExportLaneListResponse>('/api/export-docs/lanes'),
    staleTime: 30_000,
  })
}

export function useCreateExportLane() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ExportLaneInput) =>
      jsonFetch<ExportLaneCreateResponse>('/api/export-docs/lanes', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: EXPORT_LANES_KEY }),
  })
}

export function useUpdateExportLane() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { laneId: number; body: ExportLaneInput }) =>
      jsonFetch<ExportLaneCreateResponse>(`/api/export-docs/lanes/${vars.laneId}`, {
        method: 'PATCH',
        body: vars.body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: EXPORT_LANES_KEY }),
  })
}

export function useAssignExportLaneCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { laneId: number; odoo_partner_id: number; partner_name_cache?: string }) =>
      jsonFetch<{ status: string }>(`/api/export-docs/lanes/${vars.laneId}/customers`, {
        method: 'POST',
        body: {
          odoo_partner_id: vars.odoo_partner_id,
          partner_name_cache: vars.partner_name_cache,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: EXPORT_LANES_KEY }),
  })
}

export function useUnassignExportLaneCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { laneId: number; assignmentId: number }) =>
      jsonFetch<{ status: string }>(
        `/api/export-docs/lanes/${vars.laneId}/customers/${vars.assignmentId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: EXPORT_LANES_KEY }),
  })
}
